import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { MdGarage } from 'react-icons/md';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiPlus, FiTrash2, FiEdit2, FiSave, FiDownload, FiUpload, FiCalendar, FiTool, FiAlertTriangle } = FiIcons;

const UserDashboard = ({ onClose, onUpdate }) => {
  const [vehicles, setVehicles] = useState([]);
  const [isEditing, setIsEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const fileInputRef = useRef(null);

  // Load data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('bcb_user_vehicles');
    if (savedData) {
      try {
        setVehicles(JSON.parse(savedData));
      } catch (e) {
        console.error("Fehler beim Laden der Daten", e);
      }
    }
  }, []);

  // Save data to localStorage and notify parent
  useEffect(() => {
    localStorage.setItem('bcb_user_vehicles', JSON.stringify(vehicles));
    if (onUpdate) onUpdate();
  }, [vehicles, onUpdate]);

  const handleDelete = (id) => {
    if (window.confirm('Fahrzeug wirklich löschen?')) {
      setVehicles(vehicles.filter(v => v.id !== id));
    }
  };

  const handleExport = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        if (Array.isArray(data)) {
          if (window.confirm('Möchten Sie Ihre aktuelle Liste mit diesem Backup ersetzen?')) {
            setVehicles(data.slice(0, 3)); // Enforce limit
          }
        } else if (data.brand) {
          if (vehicles.length >= 3) {
            alert('Maximale Anzahl von 3 Fahrzeugen erreicht.');
            return;
          }
          setVehicles([...vehicles, { ...data, id: Date.now() }]);
        }
      } catch (err) {
        alert('Fehler beim Lesen der Datei. Ungültiges Format.');
      }
      e.target.value = ''; // Reset input
    };
    reader.readAsText(file);
  };

  const getTuvStatus = (month, year) => {
    if (!month || !year) return null;
    const nextYear = parseInt(year) + 2;
    const nextMonth = parseInt(month);
    
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-12

    const isOverdue = (currentYear > nextYear) || (currentYear === nextYear && currentMonth > nextMonth);

    return {
      nextDate: `${month}/${nextYear}`,
      isOverdue
    };
  };

  const getServiceStatus = (serviceDate) => {
    if (!serviceDate) return null;
    const lastService = new Date(serviceDate);
    const nextService = new Date(lastService);
    nextService.setFullYear(lastService.getFullYear() + 1);
    
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    nextService.setHours(0, 0, 0, 0);

    const isOverdue = now >= nextService;
    
    return {
      nextDate: nextService.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      isOverdue
    };
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-gray-800 w-full max-w-2xl rounded-2xl shadow-2xl border border-gray-700 overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <SafeIcon icon={MdGarage} className="w-6 h-6 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">Meine Garage</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-gray-700 bg-gray-800/50 flex flex-wrap gap-3 justify-between items-center">
          <div className="flex gap-2">
            <button
              onClick={() => handleExport(vehicles, 'meine_garage_backup.json')}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
              title="Alle Daten sichern"
            >
              <SafeIcon icon={FiDownload} className="w-3 h-3" /> Backup
            </button>
            <button
              onClick={handleImportClick}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
              title="Daten importieren"
            >
              <SafeIcon icon={FiUpload} className="w-3 h-3" /> Import
            </button>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".json" className="hidden" />
          </div>
          <div className="text-xs text-gray-500 font-medium">
            {vehicles.length} / 3 Fahrzeuge
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
          {showForm ? (
            <VehicleForm
              initialData={isEditing}
              onSave={(data) => {
                if (isEditing) {
                  setVehicles(vehicles.map(v => v.id === data.id ? data : v));
                } else {
                  setVehicles([...vehicles, { ...data, id: Date.now() }]);
                }
                setShowForm(false);
                setIsEditing(null);
              }}
              onCancel={() => {
                setShowForm(false);
                setIsEditing(null);
              }}
            />
          ) : (
            <>
              <AnimatePresence>
                {vehicles.length === 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12 text-gray-500">
                    <SafeIcon icon={MdGarage} className="w-16 h-16 mx-auto mb-3 opacity-20" />
                    <p>Noch keine Fahrzeuge angelegt.</p>
                  </motion.div>
                )}
                {vehicles.map((vehicle) => {
                  const serviceStatus = getServiceStatus(vehicle.serviceDate);
                  const tuvStatus = getTuvStatus(vehicle.tuvMonth, vehicle.tuvYear);
                  
                  return (
                    <motion.div
                      key={vehicle.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="bg-gray-700/30 border border-gray-600 rounded-xl p-4 hover:border-orange-500/30 transition-colors group"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-white">{vehicle.brand} {vehicle.model}</h3>
                          <span className="text-sm text-gray-400">Baujahr {vehicle.year}</span>
                        </div>
                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleExport(vehicle, `${vehicle.brand}_${vehicle.model}.json`)} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded"><SafeIcon icon={FiDownload} /></button>
                          <button onClick={() => { setIsEditing(vehicle); setShowForm(true); }} className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-gray-600 rounded"><SafeIcon icon={FiEdit2} /></button>
                          <button onClick={() => handleDelete(vehicle.id)} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-gray-600 rounded"><SafeIcon icon={FiTrash2} /></button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {/* Service Card */}
                        <div className="bg-gray-800/50 p-3 rounded-lg relative overflow-hidden">
                          <div className={`absolute top-0 left-0 w-1 h-full ${serviceStatus?.isOverdue ? 'bg-red-500' : 'bg-green-500'}`} />
                          
                          <div className="flex items-center gap-2 text-gray-400 mb-1">
                            <SafeIcon icon={FiTool} className={`w-3 h-3 ${serviceStatus?.isOverdue ? 'text-red-500' : 'text-green-500'}`} /> 
                            {serviceStatus?.isOverdue ? <span className="text-red-400 font-bold">Service fällig!</span> : 'Nächster Service'}
                          </div>
                          
                          <div className={`font-bold text-lg ${serviceStatus?.isOverdue ? 'text-red-400' : 'text-green-400'}`}>
                            {serviceStatus ? serviceStatus.nextDate : '-'}
                          </div>

                          <div className="text-xs text-gray-500 mt-1">
                            Letzter: {vehicle.serviceDate ? new Date(vehicle.serviceDate).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-'}
                          </div>
                        </div>

                        {/* TÜV Card */}
                        <div className="bg-gray-800/50 p-3 rounded-lg relative overflow-hidden">
                          <div className={`absolute top-0 right-0 w-1 h-full ${tuvStatus?.isOverdue ? 'bg-red-500' : 'bg-green-500'}`} />
                         
                          <div className="flex items-center gap-2 text-gray-400 mb-1">
                            <SafeIcon icon={FiCalendar} className={`w-3 h-3 ${tuvStatus?.isOverdue ? 'text-red-500' : 'text-green-500'}`} /> 
                            {tuvStatus?.isOverdue ? <span className="text-red-400 font-bold">TÜV abgelaufen!</span> : 'Nächster TÜV'}
                          </div>
                          
                          <div className={`font-bold text-lg ${tuvStatus?.isOverdue ? 'text-red-400' : 'text-green-400'}`}>
                            {tuvStatus ? tuvStatus.nextDate : '-'}
                          </div>
                          
                          <div className="text-xs text-gray-500 mt-1">Letzter: {vehicle.tuvMonth}/{vehicle.tuvYear}</div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
              
              {vehicles.length < 3 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowForm(true)}
                  className="w-full py-4 border-2 border-dashed border-gray-600 rounded-xl text-gray-400 hover:border-orange-500 hover:text-orange-500 transition-all flex items-center justify-center gap-2 font-medium"
                >
                  <SafeIcon icon={FiPlus} className="w-5 h-5" /> Fahrzeug hinzufügen
                </motion.button>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

const VehicleForm = ({ initialData, onSave, onCancel }) => {
  const [formData, setFormData] = useState(initialData || {
    brand: '', model: '', year: '', serviceDate: '', tuvMonth: '01', tuvYear: new Date().getFullYear()
  });

  const years = Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - i);
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
      <h3 className="text-lg font-bold text-white mb-4">{initialData ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug'}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Marke</label>
          <input
            type="text"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-orange-500 outline-none"
            value={formData.brand}
            onChange={e => setFormData({ ...formData, brand: e.target.value })}
            placeholder="z.B. Harley-Davidson"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Modell</label>
          <input
            type="text"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-orange-500 outline-none"
            value={formData.model}
            onChange={e => setFormData({ ...formData, model: e.target.value })}
            placeholder="z.B. Sportster"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Baujahr</label>
          <input
            type="number"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-orange-500 outline-none"
            value={formData.year}
            onChange={e => setFormData({ ...formData, year: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Letzter Service</label>
          <input
            type="date"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-orange-500 outline-none"
            value={formData.serviceDate}
            onChange={e => setFormData({ ...formData, serviceDate: e.target.value })}
          />
        </div>
      </div>
      
      <div className="bg-gray-700/30 p-4 rounded-lg border border-gray-600/50">
        <label className="block text-sm text-orange-400 font-medium mb-3">Letzter TÜV (Monat / Jahr)</label>
        <div className="flex gap-4">
          <select
            className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-orange-500 outline-none"
            value={formData.tuvMonth}
            onChange={e => setFormData({ ...formData, tuvMonth: e.target.value })}
          >
            {months.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select
            className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-orange-500 outline-none"
            value={formData.tuvYear}
            onChange={e => setFormData({ ...formData, tuvYear: e.target.value })}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="mt-2 text-xs text-gray-400">
          Nächster TÜV wird automatisch berechnet: <span className="text-white font-bold">{parseInt(formData.tuvYear) + 2}</span>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={onCancel}
          className="flex-1 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
        >
          Abbrechen
        </button>
        <button
          onClick={() => {
            if (!formData.brand || !formData.model) return alert('Bitte Marke und Modell ausfüllen');
            onSave(formData);
          }}
          className="flex-1 py-2 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-lg hover:from-orange-700 hover:to-orange-600 transition-colors font-medium flex justify-center items-center gap-2"
        >
          <SafeIcon icon={FiSave} className="w-4 h-4" /> Speichern
        </button>
      </div>
    </motion.div>
  );
};

export default UserDashboard;